import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useDebounce } from './useDebounce'
import { fetchDeletedPlans, restorePlan } from '@/api/deletedPlans'
import type { PaginatedPlansResponse, ApiPlanDetailResponse } from '@/types'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'

interface ApiErrorPayload {
  detail?: string
  title?: string
}

function getApiErrorMessage(
  error: AxiosError<ApiErrorPayload>,
  fallback: string
) {
  return error.response?.data?.detail ?? error.response?.data?.title ?? fallback
}

// --- List deleted plans ---

interface UseDeletedPlansListParams {
  search: string
  deletedAfter?: string
  deletedBefore?: string
  page: number
  pageSize: number
}

export function useDeletedPlansList({
  search,
  deletedAfter,
  deletedBefore,
  page,
  pageSize,
}: UseDeletedPlansListParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    deletedAfter,
    deletedBefore,
    page,
    pageSize,
  }

  return useQuery<PaginatedPlansResponse>({
    queryKey: queryKeys.deletedPlans.list(filters),
    queryFn: () =>
      fetchDeletedPlans({
        search: debouncedSearch || undefined,
        deleted_after: deletedAfter || undefined,
        deleted_before: deletedBefore || undefined,
        offset: (page - 1) * pageSize,
        limit: pageSize,
      }),
  })
}

// --- Restore plan ---

export function useRestorePlan() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiPlanDetailResponse,
    AxiosError<ApiErrorPayload>,
    { planId: string }
  >({
    mutationFn: ({ planId }) => restorePlan(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.deletedPlans.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.assortmentPlans.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.adminPlans.all })
      toast.success('Plan restored successfully')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to restore plan'))
    },
  })
}
